import React, { useState } from "react";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

/**
 * PhoneEmailDashboard.jsx — OSL morning trigger for second-period phone emails.
 *
 * Mount behind your staff/ProtectedRoute (or a ToolRoute gated on a granted
 * tool). It calls the sendPhoneNotTurnedIn callable: Preview runs a dry run and
 * shows exactly which teachers would be emailed which students; Send emails does
 * the live send after a confirm. Test mode is controlled server-side
 * (TEST_MODE_DEFAULT); while it's on, subjects get a [TEST] prefix.
 */

const NAVY = "#233C5F";
const RED = "#E83530";
const GOLD = "#A29164";
const GREEN = "#2e7d32";
const BEIGE = "#F5F0E6";
const INK = "#1f2733";
const MUTE = "#6b7280";
const LINE = "#e5e7eb";

function ammanToday() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Amman", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

export default function PhoneEmailDashboard() {
  const fns = getFunctions(getApp(), "us-central1");
  const call = httpsCallable(fns, "sendPhoneNotTurnedIn");

  const [date, setDate] = useState(ammanToday());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // last response
  const [wasDryRun, setWasDryRun] = useState(true);
  const [error, setError] = useState("");

  async function run(dryRun, force = false) {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const res = await call({ date, dryRun, force });
      setResult(res.data);
      setWasDryRun(dryRun);
    } catch (e) {
      setError(e.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  function confirmSend() {
    const teachers = result?.teacherCount ?? 0;
    if (!window.confirm(`Send phone-not-turned-in emails to ${teachers} teacher(s) for ${date}? This sends real email.`)) return;
    run(false, false);
  }

  const teachers = (result?.results || []).filter((r) => (r.count || 0) > 0 || r.sent || r.reason !== "no email on staff record" || true);
  const previewRows = result?.results || [];
  const totalStudents = previewRows.reduce((s, r) => s + (r.count || 0), 0);

  return (
    <div style={S.root}>
      <style>{css}</style>

      <header style={S.head}>
        <div>
          <h1 style={S.h1}>Second-Period Phone Emails</h1>
          <p style={S.sub}>King's Academy · Office of Student Life</p>
        </div>
        <label style={S.dateWrap}>
          <span style={S.dateLbl}>Date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={S.date} />
        </label>
      </header>

      <p style={S.blurb}>
        After the morning phone count is in, use <strong>Preview</strong> to see which second-period
        teachers would be emailed and who's on each list. When it looks right, use <strong>Send emails</strong>.
      </p>

      <div style={S.actions}>
        <button onClick={() => run(true)} disabled={busy} style={S.btnGhost}>
          {busy ? "Working…" : "Preview (no email sent)"}
        </button>
        <button
          onClick={confirmSend}
          disabled={busy || !result || wasDryRun !== true || (result?.teacherCount ?? 0) === 0}
          style={{ ...S.btn, opacity: !result || wasDryRun !== true || (result?.teacherCount ?? 0) === 0 ? 0.5 : 1 }}
          title={!result ? "Preview first" : ""}
        >
          Send emails
        </button>
      </div>

      {error && <div style={S.error}>{error}</div>}

      {result && (
        <div style={S.card}>
          <div style={S.resultHead}>
            <div>
              <span style={{ ...S.badge, background: wasDryRun ? "#eef2f7" : "#e7f2e8", color: wasDryRun ? NAVY : GREEN }}>
                {wasDryRun ? "Preview" : "Sent"}
              </span>
              {result.testMode && <span style={{ ...S.badge, background: "#fdece9", color: RED, marginLeft: 8 }}>Test mode</span>}
            </div>
            <div style={S.summary}>{result.message}</div>
          </div>

          {result.secondPeriodBlock && (
            <div style={S.metaRow}>
              <Metric label="Second period" value={`Block ${result.secondPeriodBlock}`} />
              <Metric label="Teachers" value={result.teacherCount ?? 0} />
              <Metric label="Students" value={totalStudents} />
              {!wasDryRun && <Metric label="Emails sent" value={result.emailsSent ?? 0} color={GREEN} />}
            </div>
          )}

          {previewRows.length > 0 ? (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Teacher</th>
                  <th style={S.th}>Class</th>
                  <th style={{ ...S.th, textAlign: "right" }}>#</th>
                  <th style={S.th}>Students</th>
                  <th style={S.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((r, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${LINE}` }}>
                    <td style={S.td}>{r.teacherName || r.teacherId}</td>
                    <td style={S.td}>{r.course || ""}</td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: NAVY }}>{r.count || 0}</td>
                    <td style={{ ...S.td, color: MUTE, fontSize: 13 }}>{(r.students || []).join(", ")}</td>
                    <td style={S.td}>
                      {r.sent ? <span style={{ color: GREEN, fontWeight: 600 }}>Sent</span>
                        : r.reason === "dry-run" ? <span style={{ color: MUTE }}>Would send</span>
                        : <span style={{ color: RED }}>{r.reason || "Not sent"}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={S.empty}>{result.message}</p>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div style={S.metric}>
      <div style={S.metricLbl}>{label}</div>
      <div style={{ ...S.metricVal, color: color || NAVY }}>{value}</div>
    </div>
  );
}

const S = {
  root: { fontFamily: "Calibri,'Segoe UI',system-ui,sans-serif", color: INK, background: BEIGE, minHeight: "100vh", padding: "24px 28px", maxWidth: 1000, margin: "0 auto" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, borderBottom: `3px solid ${NAVY}`, paddingBottom: 12, flexWrap: "wrap" },
  h1: { fontFamily: "Georgia,serif", color: NAVY, margin: 0, fontSize: 26 },
  sub: { margin: "2px 0 0", color: GOLD, fontSize: 13, letterSpacing: ".06em", textTransform: "uppercase" },
  dateWrap: { display: "flex", flexDirection: "column", gap: 3 },
  dateLbl: { fontSize: 12, color: MUTE },
  date: { fontSize: 14, padding: "7px 10px", border: `1px solid ${LINE}`, borderRadius: 8 },
  blurb: { fontSize: 14, color: "#5b6472", margin: "16px 0" },
  actions: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 },
  btn: { fontSize: 15, fontWeight: 600, padding: "10px 22px", border: "none", borderRadius: 8, background: NAVY, color: "#fff", cursor: "pointer" },
  btnGhost: { fontSize: 15, fontWeight: 600, padding: "10px 22px", border: `1px solid ${NAVY}`, borderRadius: 8, background: "#fff", color: NAVY, cursor: "pointer" },
  error: { background: "#fff5f5", border: `1px solid ${RED}`, color: RED, borderRadius: 8, padding: "10px 14px", fontSize: 14, marginBottom: 16 },
  card: { background: "#fff", border: `1px solid ${LINE}`, borderRadius: 12, padding: 18 },
  resultHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 },
  badge: { display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700 },
  summary: { fontSize: 14, color: "#5b6472" },
  metaRow: { display: "flex", gap: 24, flexWrap: "wrap", margin: "6px 0 16px" },
  metric: {},
  metricLbl: { fontSize: 12, color: MUTE },
  metricVal: { fontSize: 22, fontWeight: 800 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: { textAlign: "left", padding: "8px 10px", fontSize: 12, color: MUTE, textTransform: "uppercase", letterSpacing: ".05em", borderBottom: `2px solid ${LINE}` },
  td: { padding: "8px 10px", verticalAlign: "top" },
  empty: { color: MUTE, fontSize: 14 },
};

const css = `
input[type=date]{font-family:inherit;}
button:disabled{cursor:not-allowed;}
`;